import {
    WeatherStationDataService,
    WeatherStationQueryParams,
    WeatherStationResponse,
    CreateWeatherStationRequest,
    UpdateWeatherStationRequest,
    WeatherStationServiceError,
    CFFDRSStartParams
} from '../types/weatherStation';
import { WeatherStationEntityEntry } from '../domain/model/weatherStation.js';
import { getBackendUrl, isDevMode } from './configService';

export class WeatherStationService implements WeatherStationDataService {
    private getBaseUrl(): string {
        const backendUrl = getBackendUrl();
        if (!backendUrl) {
            throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
        }
        return `${backendUrl}/api/weather-stations`;
    }

    private getAuthToken(): string | null {
        const accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
            if (isDevMode()) {
                return 'dev-mode-token';
            } else {
                console.log('🚨 No access token found in production mode. Fetch will not be authenticated via Bearer token.');
                return null;
            }
        }
        return accessToken;
    }

    async fetchData(params?: WeatherStationQueryParams): Promise<WeatherStationResponse> {
        try {
            const queryString = this.buildQueryString(params);
            const url = `${this.getBaseUrl()}${queryString}`;
            const token = this.getAuthToken();

            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                throw await this.handleErrorResponse(response);
            }

            return await response.json();
        } catch (error) {
            throw this.handleServiceError(error);
        }
    }

    async createStation(data: CreateWeatherStationRequest): Promise<WeatherStationEntityEntry> {
        try {
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(this.getBaseUrl(), {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                credentials: 'include',
            });

            if (!response.ok) {
                console.error('[WeatherStationService] createStation: Response not OK', response.status, response.statusText);
                throw await this.handleErrorResponse(response);
            }

            const result = await response.json();
            console.log('[WeatherStationService] createStation: Success, response:', result);
            return result;
        } catch (error) {
            console.error('[WeatherStationService] createStation: Error', error);
            throw this.handleServiceError(error);
        }
    }

    async updateStation(id: string, data: UpdateWeatherStationRequest): Promise<WeatherStationEntityEntry> {
        try {
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
                credentials: 'include',
            });

            if (!response.ok) {
                throw await this.handleErrorResponse(response);
            }

            return await response.json();
        } catch (error) {
            throw this.handleServiceError(error);
        }
    }

    async deleteStation(id: string): Promise<void> {
        try {
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'DELETE',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                throw await this.handleErrorResponse(response);
            }
        } catch (error) {
            throw this.handleServiceError(error);
        }
    }

    async getStationById(id: string): Promise<WeatherStationEntityEntry> {
        try {
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                throw await this.handleErrorResponse(response);
            }

            return await response.json();
        } catch (error) {
            throw this.handleServiceError(error);
        }
    }

    async getClosingDCOptions(stationId: string): Promise<any> {
        const backendUrl = getBackendUrl();
        if (!backendUrl) {
            throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
        }
        const token = this.getAuthToken();
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${backendUrl}/api/weather-stations/${stationId}/cffdrs/closing-dc-options`, {
            method: 'GET',
            headers,
            credentials: 'include',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error((error as any).message || 'Failed to fetch closing DC options');
        }
        return response.json();
    }

    async startCFFDRS(stationId: string, params: CFFDRSStartParams): Promise<any> {
        const backendUrl = getBackendUrl();
        if (!backendUrl) {
            throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
        }
        const token = this.getAuthToken();
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${backendUrl}/api/weather-stations/${stationId}/cffdrs/start`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(params),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error((error as any).message || 'Failed to start CFFDRS');
        }
        return response.json();
    }

    private buildQueryString(params?: WeatherStationQueryParams): string {
        if (!params) return '';

        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                searchParams.append(key, value.toString());
            }
        });

        const queryString = searchParams.toString();
        return queryString ? `?${queryString}` : '';
    }

    private async handleErrorResponse(response: Response): Promise<WeatherStationServiceError> {
        let errorData;
        try {
            errorData = await response.json();
        } catch {
            errorData = { message: 'Unknown error occurred' };
        }

        const serviceError: WeatherStationServiceError = {
            type: this.getErrorType(response.status),
            message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            statusCode: response.status,
            errors: errorData.errors,
        };

        return serviceError;
    }

    private handleServiceError(error: any): WeatherStationServiceError {
        if (error.type && error.message) {
            // Already a WeatherStationServiceError
            return error;
        }

        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                type: 'NETWORK_ERROR',
                message: 'Network error: Unable to connect to the server',
            };
        }

        return {
            type: 'SERVER_ERROR',
            message: error.message || 'An unexpected error occurred',
        };
    }

    private getErrorType(statusCode: number): WeatherStationServiceError['type'] {
        switch (statusCode) {
            case 400:
                return 'VALIDATION_ERROR';
            case 401:
                return 'UNAUTHORIZED';
            case 404:
                return 'NOT_FOUND';
            case 500:
            default:
                return 'SERVER_ERROR';
        }
    }
}